// CategorySlider.tsx
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  FC,
} from "react";
import { gsap } from "gsap";
import { twMerge } from "tailwind-merge"; // Ensure you have tailwind-merge installed

// Define the Props Interface
interface CategorySliderProps {
  categories: string[];
  selectedCategory: string | null;
  onCategoryClick: (category: string) => void;
}

// Define the Ref Interface
export interface CategorySliderRef {
  bringCategoryIntoView: (category: string) => void;
}

// Define a separate CategoryItem component for better maintainability
interface CategoryItemProps {
  category: string;
  isSelected: boolean;
  isBringingIntoView: boolean;
  onClick: () => void;
  refCallback: (el: HTMLDivElement | null) => void;
}

const CategoryItem: FC<CategoryItemProps> = ({
  category,
  isSelected,
  isBringingIntoView,
  onClick,
  refCallback,
}) => {
  const baseClasses =
    "font-sans flex-shrink-0 px-4 py-2 rounded-full cursor-pointer text-xs whitespace-nowrap shadow-xs transition-all duration-300 ease-in-out select-none";

  const activeClasses = "bg-primary text-white border border-white/50";

  const bringIntoViewClasses =
    "bg-primary/70 text-white border border-white/50"; // More transparent

  const defaultClasses =
    "bg-white text-primary hover:bg-white/70 hover:shadow-md";

  const combinedClasses = twMerge(
    baseClasses,
    isSelected
      ? activeClasses
      : isBringingIntoView
        ? bringIntoViewClasses
        : defaultClasses,
  );

  return (
    <div ref={refCallback} onClick={onClick} className={combinedClasses}>
      {category}
    </div>
  );
};

// Modify the component to use forwardRef
const CategorySlider = forwardRef<CategorySliderRef, CategorySliderProps>(
  ({ categories, selectedCategory, onCategoryClick }, ref) => {
    const [isAtStart, setIsAtStart] = useState<boolean>(true);
    const [isAtEnd, setIsAtEnd] = useState<boolean>(false);
    const [bringingIntoView, setBringingIntoView] = useState<string | null>(
      null,
    );

    const sliderRef = useRef<HTMLDivElement | null>(null);
    const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Function to check scroll position
    const checkScrollPosition = () => {
      if (sliderRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
        setIsAtStart(scrollLeft === 0);
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1); // -1 to account for minor discrepancies
      }
    };

    // Attach scroll event listener
    useEffect(() => {
      const slider = sliderRef.current;
      if (slider) {
        slider.addEventListener("scroll", checkScrollPosition);
        checkScrollPosition(); // Initial check
      }

      // Cleanup on unmount
      return () => {
        if (slider) slider.removeEventListener("scroll", checkScrollPosition);
      };
    }, [categories]);

    // Function to handle scrolling with GSAP
    const scrollSlider = (direction: "left" | "right") => {
      if (sliderRef.current) {
        const distance = 200; // Amount to scroll in pixels
        const newScrollLeft =
          direction === "left"
            ? sliderRef.current.scrollLeft - distance
            : sliderRef.current.scrollLeft + distance;

        gsap.to(sliderRef.current, {
          scrollLeft: newScrollLeft,
          duration: 0.5,
          ease: "power2.out",
        });
      }
    };

    // Function to bring a specific category into view
    const bringCategoryIntoView = (category: string) => {
      const categoryElement = categoryRefs.current[category];
      const sliderElement = sliderRef.current;

      if (categoryElement && sliderElement) {
        const categoryRect = categoryElement.getBoundingClientRect();
        const sliderRect = sliderElement.getBoundingClientRect();

        const offset =
          categoryRect.left -
          sliderRect.left -
          sliderRect.width / 2 +
          categoryRect.width / 2;

        gsap.to(sliderElement, {
          scrollLeft: sliderElement.scrollLeft + offset,
          duration: 0.5,
          ease: "power2.out",
          onStart: () => setBringingIntoView(category),
          onComplete: () => setBringingIntoView(null),
        });
      }
    };

    // Expose bringCategoryIntoView via ref
    useImperativeHandle(ref, () => ({
      bringCategoryIntoView,
    }));

    // Effect to bring the selected category into view when it changes
    useEffect(() => {
      if (selectedCategory) {
        bringCategoryIntoView(selectedCategory);
      }
    }, [selectedCategory]);

    return (
      <div className="w-full fixed bottom-0 backdrop-blur z-20 py-4 px-2 flex items-center">
        {/* Left Arrow */}
        <button
          type="button"
          className={twMerge(
            "absolute left-4 cursor-pointer flex-shrink-0 p-2 rounded-full bg-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] transition-all",
            isAtStart
              ? "opacity-80 pointer-events-none" // Disabled styles
              : "hover:bg-white/70 shadow-xs",
          )}
          onClick={() => !isAtStart && scrollSlider("left")}
          disabled={isAtStart}
          aria-label="Scroll Left"
        >
          <span className="d-icon d-arrowhead-left text-primary flex items-center"></span>
        </button>

        {/* Category Slider */}
        <div
          ref={sliderRef}
          className="flex overflow-x-auto categories-slider rounded-full no-scrollbar space-x-2 mx-2 items-center"
        >
          {categories.map((category) => (
            <CategoryItem
              key={category}
              category={category}
              isSelected={selectedCategory === category}
              isBringingIntoView={bringingIntoView === category}
              onClick={() => onCategoryClick(category)}
              refCallback={(el) => (categoryRefs.current[category] = el)}
            />
          ))}
        </div>

        {/* Right Arrow */}
        <button
          type="button"
          className={twMerge(
            "absolute right-4 cursor-pointer flex-shrink-0 p-2 rounded-full bg-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] transition-all",
            isAtEnd
              ? "opacity-80 pointer-events-none" // Disabled styles
              : "hover:bg-white/70 shadow-xs",
          )}
          onClick={() => !isAtEnd && scrollSlider("right")}
          disabled={isAtEnd}
          aria-label="Scroll Right"
        >
          <span className="d-icon d-arrowhead-right text-primary flex items-center"></span>
        </button>
      </div>
    );
  },
);

export default CategorySlider;
